/**
 * FinancialsTab — Income statement, related parties, and balance sheet
 * pulled directly from the cost reports behind this report.
 *
 * Sources (CMS form 2540-10): Worksheet G-3 (income statement),
 * Worksheet A-8-1 Parts I/II (related parties), Worksheet G (balance sheet).
 * Tables mirror the "Related Parties" tab of the generated Financial and
 * Staffing Analysis workbook: Over/Under Payment = Payment − Allowable Cost,
 * so positive = paid above allowable cost.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import api from '../../api/axios';

/* ───────────── types ───────────── */

interface IncomeStatement {
  total_patient_revenues: number | null;
  contractual_allowances: number | null;
  net_patient_revenues: number | null;
  total_operating_expenses: number | null;
  net_income: number | null;
}

interface BalanceSheet {
  cash_on_hand: number | null;
  due_from_other_funds: number | null;
  total_current_assets: number | null;
  total_fixed_assets: number | null;
  total_other_assets: number | null;
  total_assets: number | null;
  total_current_liabilities: number | null;
  notes_and_loans_payable: number | null;
  due_to_other_funds: number | null;
  total_long_term_liabilities: number | null;
  total_liabilities: number | null;
  general_fund_balance: number | null;
  total_fund_balances: number | null;
  total_liabilities_and_fund_balances: number | null;
}

interface RelatedPartyCost {
  line: string;
  cost_center: string | null;
  expense_item: string | null;
  allowable_cost: number;
  payment: number;
  over_under: number;
}

interface RelatedPartyTotals {
  allowable_cost: number;
  payment: number;
  over_under: number;
}

interface RelatedOrganization {
  symbol: string | null;
  name: string | null;
  pct_ownership: string | null;
  related_name: string | null;
  related_pct_ownership: string | null;
  business_type: string | null;
}

interface FinancialReport {
  rpt_rec_num: number;
  fy_bgn_dt: string | null;
  fy_end_dt: string | null;
  fiscal_year: number | null;
  period_label: string;
  income_statement: IncomeStatement;
  balance_sheet: BalanceSheet;
  related_party_costs: RelatedPartyCost[];
  related_party_totals: RelatedPartyTotals;
  related_organizations: RelatedOrganization[];
}

interface Props {
  ccn: string;
  reportIds: number[];
}

/* ───────────── formatting ───────────── */

// Accounting style: negatives in parentheses, matching the Excel workbook.
const fmtMoney = (v: number | null | undefined): string => {
  if (v == null) return '—';
  const s = `$${Math.abs(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  return v < 0 ? `(${s})` : s;
};

const fmtCompact = (v: number): string => {
  const abs = Math.abs(v);
  const s =
    abs >= 1_000_000 ? `$${(abs / 1_000_000).toFixed(1)}M`
    : abs >= 1_000 ? `$${Math.round(abs / 1_000)}K`
    : `$${Math.round(abs)}`;
  return v < 0 ? `(${s})` : s;
};

// Short axis label; two of a report's periods can end in the same year
// (stub period after a sale), so stubs keep their date range.
const shortLabel = (r: FinancialReport): string => {
  if (r.period_label.includes('–')) {
    const two = (iso: string | null) => {
      if (!iso) return '';
      const [y, m, d] = iso.split('-');
      return `${+m}/${+d}/${y.slice(2)}`;
    };
    return `${two(r.fy_bgn_dt)}–${two(r.fy_end_dt)}`;
  }
  return r.period_label;
};

/* ───────────── chart primitives ───────────── */

const TEAL = '#10A4C2';
const ORANGE = '#E8873A';
const RED = '#d32f2f';

// Bar with only the end away from the baseline rounded.
function barPath(x: number, w: number, yZero: number, yVal: number, r = 4): string {
  if (Math.abs(yVal - yZero) < 0.5) {
    return `M ${x} ${yZero - 1} H ${x + w} V ${yZero + 1} H ${x} Z`;
  }
  if (yVal < yZero) {
    const rr = Math.min(r, yZero - yVal, w / 2);
    return [
      `M ${x} ${yZero}`, `L ${x} ${yVal + rr}`, `Q ${x} ${yVal} ${x + rr} ${yVal}`,
      `L ${x + w - rr} ${yVal}`, `Q ${x + w} ${yVal} ${x + w} ${yVal + rr}`,
      `L ${x + w} ${yZero}`, 'Z',
    ].join(' ');
  }
  const rr = Math.min(r, yVal - yZero, w / 2);
  return [
    `M ${x} ${yZero}`, `L ${x} ${yVal - rr}`, `Q ${x} ${yVal} ${x + rr} ${yVal}`,
    `L ${x + w - rr} ${yVal}`, `Q ${x + w} ${yVal} ${x + w} ${yVal - rr}`,
    `L ${x + w} ${yZero}`, 'Z',
  ].join(' ');
}

interface BarSeries {
  label: string;
  color: string;
  // color override per value (e.g. red for negative net income)
  colorFor?: (v: number) => string;
  values: (number | null)[];
}

interface GroupedBarChartProps {
  title: string;
  groups: string[]; // x labels, one per period
  tooltipTitles: string[]; // full period label per group
  series: BarSeries[];
  height?: number;
}

/**
 * Small grouped bar chart (≤3 groups, 1-2 series) with zero baseline,
 * direct value labels, and per-bar tooltips.
 */
const GroupedBarChart: React.FC<GroupedBarChartProps> = ({ title, groups, tooltipTitles, series, height = 220 }) => {
  const all = series.flatMap(s => s.values).filter((v): v is number => v != null);
  if (all.length === 0) return null;

  const svgWidth = 380;
  // Negative bars carry their value label below the bar end — reserve room
  // so it clears the x-axis labels.
  const padding = { left: 12, right: 12, top: 28, bottom: all.some(v => v < 0) ? 44 : 26 };
  const plotWidth = svgWidth - padding.left - padding.right;
  const plotHeight = height - padding.top - padding.bottom;
  const maxVal = Math.max(...all, 0) * 1.12 || 1;
  const minVal = Math.min(...all, 0) * 1.12;
  const scaleY = (v: number) =>
    padding.top + plotHeight - ((v - minVal) / (maxVal - minVal)) * plotHeight;
  const yZero = scaleY(0);

  const groupWidth = plotWidth / groups.length;
  const barW = Math.min(36, (groupWidth - 16) / series.length - 2);
  const clusterW = series.length * barW + (series.length - 1) * 2;

  return (
    <Box>
      <Typography variant="caption" color="text.secondary" fontWeight={600}>{title}</Typography>
      <svg width="100%" height={height} viewBox={`0 0 ${svgWidth} ${height}`} preserveAspectRatio="xMidYMid meet">
        {/* zero baseline */}
        <line x1={padding.left} y1={yZero} x2={svgWidth - padding.right} y2={yZero} stroke="#bdbdbd" strokeWidth={1} />

        {groups.map((g, gi) => {
          const clusterX = padding.left + gi * groupWidth + (groupWidth - clusterW) / 2;
          return (
            <g key={gi}>
              {series.map((s, si) => {
                const v = s.values[gi];
                if (v == null) return null;
                const x = clusterX + si * (barW + 2);
                const y = scaleY(v);
                const color = s.colorFor ? s.colorFor(v) : s.color;
                return (
                  <Tooltip
                    key={si}
                    arrow
                    title={
                      <Box sx={{ fontFamily: 'monospace', fontSize: '12px' }}>
                        <Box fontWeight={600} mb={0.5}>{tooltipTitles[gi]}</Box>
                        <Box>{s.label}: {fmtMoney(v)}</Box>
                      </Box>
                    }
                  >
                    <g style={{ cursor: 'pointer' }}>
                      {/* enlarged hit target */}
                      <rect x={x - 3} y={padding.top} width={barW + 6} height={plotHeight} fill="transparent" />
                      <path d={barPath(x, barW, yZero, y)} fill={color} />
                      <text
                        x={x + barW / 2}
                        y={v >= 0 ? y - 5 : y + 12}
                        textAnchor="middle"
                        fontSize={10}
                        fontWeight={600}
                        fill="#455a64"
                      >
                        {fmtCompact(v)}
                      </text>
                    </g>
                  </Tooltip>
                );
              })}
              <text
                x={padding.left + gi * groupWidth + groupWidth / 2}
                y={height - 8}
                textAnchor="middle"
                fontSize={10}
                fill="#666"
                fontWeight={600}
              >
                {g}
              </text>
            </g>
          );
        })}
      </svg>
      {series.length > 1 && (
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 0.5 }}>
          {series.map((s, i) => (
            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Box sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: s.color }} />
              <Typography variant="caption" color="text.secondary">{s.label}</Typography>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

/* ───────────── table helpers ───────────── */

const headerCellSx = { fontWeight: 700, color: '#102B3D', bgcolor: '#EAF1F5' };
const labelCellSx = { color: '#374151' };
const numCellSx = { fontVariantNumeric: 'tabular-nums' };
const totalRowSx = { '& td': { fontWeight: 700, borderTop: '2px solid #D7E1E8' } };

const SectionCard: React.FC<{ title: string; source: string; children: React.ReactNode }> = ({ title, source, children }) => (
  <Box sx={{ px: 2.5, py: 2.5, backgroundColor: 'white', borderRadius: 2, border: '1px solid', borderColor: 'divider', mb: 3 }}>
    <Box mb={2}>
      <Typography variant="h6" fontWeight={700}>{title}</Typography>
      <Typography variant="caption" color="text.secondary">Source: {source}</Typography>
    </Box>
    {children}
  </Box>
);

/** Line-item table with one column per period. */
const StatementTable: React.FC<{
  periods: string[];
  rows: { label: string; values: (number | null)[]; indent?: boolean; total?: boolean; section?: boolean }[];
}> = ({ periods, rows }) => (
  <TableContainer sx={{ overflowX: 'auto' }}>
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell sx={headerCellSx} />
          {periods.map((p, i) => (
            <TableCell key={i} align="right" sx={headerCellSx}>{p}</TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((row, i) =>
          row.section ? (
            <TableRow key={i}>
              <TableCell colSpan={periods.length + 1} sx={{ fontWeight: 700, color: '#102B3D', bgcolor: '#F6F9FB' }}>
                {row.label}
              </TableCell>
            </TableRow>
          ) : (
            <TableRow key={i} sx={row.total ? totalRowSx : undefined}>
              <TableCell sx={{ ...labelCellSx, pl: row.indent ? 4 : 2 }}>{row.label}</TableCell>
              {row.values.map((v, j) => (
                <TableCell key={j} align="right" sx={numCellSx}>{fmtMoney(v)}</TableCell>
              ))}
            </TableRow>
          )
        )}
      </TableBody>
    </Table>
  </TableContainer>
);

/* ───────────── main component ───────────── */

const FinancialsTab: React.FC<Props> = ({ ccn, reportIds }) => {
  const [loading, setLoading] = useState(true);
  const [reports, setReports] = useState<FinancialReport[]>([]);
  const [error, setError] = useState(false);
  const [selectedRpt, setSelectedRpt] = useState<number | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/facilities/${ccn}/financials/`, {
        params: { report_ids: reportIds.join(',') },
      });
      const fetched: FinancialReport[] = res.data.reports || [];
      setReports(fetched);
      if (fetched.length) setSelectedRpt(fetched[fetched.length - 1].rpt_rec_num);
    } catch (err) {
      console.error('Error fetching financials:', err);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [ccn, reportIds]);

  useEffect(() => {
    if (ccn && reportIds.length) fetchData();
    else setLoading(false);
  }, [ccn, reportIds, fetchData]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || reports.length === 0 || !reportIds.length) {
    return (
      <Alert severity="info">
        No cost report financial data is available for this report.
      </Alert>
    );
  }

  const periods = reports.map(r => r.period_label);
  const short = reports.map(shortLabel);
  const inc = (k: keyof IncomeStatement) => reports.map(r => r.income_statement[k]);
  const bs = (k: keyof BalanceSheet) => reports.map(r => r.balance_sheet[k]);

  const selected = reports.find(r => r.rpt_rec_num === selectedRpt) || reports[reports.length - 1];

  return (
    <Box>
      {/* ── Income Statement ── */}
      <SectionCard title="Income Statement" source="CMS 2540-10, Worksheet G-3">
        <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          <Box sx={{ flex: '1 1 480px', minWidth: 360 }}>
            <StatementTable
              periods={periods}
              rows={[
                { label: 'Total Patient Revenues', values: inc('total_patient_revenues') },
                { label: 'Contractual Allowances', values: inc('contractual_allowances') },
                { label: 'Net Patient Revenues', values: inc('net_patient_revenues'), total: true },
                { label: 'Total Operating Expenses', values: inc('total_operating_expenses') },
                { label: 'Net Income', values: inc('net_income'), total: true },
              ]}
            />
          </Box>
          <Box sx={{ flex: '0 1 380px', minWidth: 300 }}>
            <GroupedBarChart
              title="NET INCOME BY PERIOD"
              groups={short}
              tooltipTitles={periods}
              series={[{
                label: 'Net Income',
                color: TEAL,
                colorFor: (v) => (v < 0 ? RED : TEAL),
                values: inc('net_income'),
              }]}
            />
          </Box>
        </Box>
      </SectionCard>

      {/* ── Related Parties ── */}
      <SectionCard title="Related Parties" source="CMS 2540-10, Worksheet A-8-1">
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ color: '#102B3D' }}>
            Payments to Related Parties
          </Typography>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={selected.rpt_rec_num}
            onChange={(_e, v) => v != null && setSelectedRpt(v)}
          >
            {reports.map((r, i) => (
              <ToggleButton key={r.rpt_rec_num} value={r.rpt_rec_num} sx={{ textTransform: 'none', px: 1.5 }}>
                {short[i]}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>

        {selected.related_party_costs.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            No related party transactions reported for this period.
          </Typography>
        ) : (
          <TableContainer sx={{ overflowX: 'auto', mb: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={headerCellSx}>Cost Center</TableCell>
                  <TableCell sx={headerCellSx}>Expense Item</TableCell>
                  <TableCell align="right" sx={headerCellSx}>Allowable Cost</TableCell>
                  <TableCell align="right" sx={headerCellSx}>Payment</TableCell>
                  <TableCell align="right" sx={headerCellSx}>Over / Under Payment</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {selected.related_party_costs.map((row, i) => (
                  <TableRow key={i} hover>
                    <TableCell sx={labelCellSx}>{row.cost_center || '—'}</TableCell>
                    <TableCell sx={labelCellSx}>{row.expense_item || '—'}</TableCell>
                    <TableCell align="right" sx={numCellSx}>{fmtMoney(row.allowable_cost)}</TableCell>
                    <TableCell align="right" sx={numCellSx}>{fmtMoney(row.payment)}</TableCell>
                    <TableCell align="right" sx={{ ...numCellSx, color: row.over_under > 0 ? RED : undefined, fontWeight: row.over_under > 0 ? 600 : undefined }}>
                      {fmtMoney(row.over_under)}
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow sx={totalRowSx}>
                  <TableCell colSpan={2}>Total</TableCell>
                  <TableCell align="right" sx={numCellSx}>{fmtMoney(selected.related_party_totals.allowable_cost)}</TableCell>
                  <TableCell align="right" sx={numCellSx}>{fmtMoney(selected.related_party_totals.payment)}</TableCell>
                  <TableCell align="right" sx={{ ...numCellSx, color: selected.related_party_totals.over_under > 0 ? RED : undefined }}>
                    {fmtMoney(selected.related_party_totals.over_under)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          <Box sx={{ flex: '1 1 480px', minWidth: 360 }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ color: '#102B3D', mb: 1.5 }}>
              Related Organizations
            </Typography>
            {selected.related_organizations.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No related organizations reported for this period.
              </Typography>
            ) : (
              <TableContainer sx={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={headerCellSx}>Name</TableCell>
                      <TableCell align="right" sx={headerCellSx}>% Ownership</TableCell>
                      <TableCell sx={headerCellSx}>Related Organization</TableCell>
                      <TableCell align="right" sx={headerCellSx}>% Ownership</TableCell>
                      <TableCell sx={headerCellSx}>Type of Business</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selected.related_organizations.map((org, i) => (
                      <TableRow key={i} hover>
                        <TableCell sx={labelCellSx}>{org.name || '—'}</TableCell>
                        <TableCell align="right">{org.pct_ownership || '—'}</TableCell>
                        <TableCell sx={labelCellSx}>{org.related_name || '—'}</TableCell>
                        <TableCell align="right">{org.related_pct_ownership || '—'}</TableCell>
                        <TableCell sx={labelCellSx}>{org.business_type || '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
          <Box sx={{ flex: '0 1 380px', minWidth: 300 }}>
            <GroupedBarChart
              title="RELATED PARTY PAYMENTS BY PERIOD"
              groups={short}
              tooltipTitles={periods}
              series={[
                { label: 'Total Payments', color: TEAL, values: reports.map(r => r.related_party_totals.payment) },
                { label: 'Over/Under Payment', color: ORANGE, values: reports.map(r => r.related_party_totals.over_under) },
              ]}
            />
          </Box>
        </Box>
      </SectionCard>

      {/* ── Balance Sheet ── */}
      <SectionCard title="Balance Sheet" source="CMS 2540-10, Worksheet G">
        <StatementTable
          periods={periods}
          rows={[
            { label: 'Current Assets', values: [], section: true },
            { label: 'Cash on Hand', values: bs('cash_on_hand'), indent: true },
            { label: 'Due from Other Funds', values: bs('due_from_other_funds'), indent: true },
            { label: 'Total Current Assets', values: bs('total_current_assets'), total: true },
            { label: 'Total Fixed Assets', values: bs('total_fixed_assets'), total: true },
            { label: 'Total Other Assets', values: bs('total_other_assets'), total: true },
            { label: 'Total Assets', values: bs('total_assets'), total: true },
            { label: 'Liabilities', values: [], section: true },
            { label: 'Total Current Liabilities', values: bs('total_current_liabilities') },
            { label: 'Notes and Loans Payable', values: bs('notes_and_loans_payable'), indent: true },
            { label: 'Due to Other Funds', values: bs('due_to_other_funds'), indent: true },
            { label: 'Total Long-Term Liabilities', values: bs('total_long_term_liabilities') },
            { label: 'Total Liabilities', values: bs('total_liabilities'), total: true },
            { label: 'Capital Accounts', values: [], section: true },
            { label: 'General Fund Balance', values: bs('general_fund_balance'), indent: true },
            { label: 'Total Fund Balances', values: bs('total_fund_balances'), indent: true, total: true },
            { label: 'Total Liabilities and Fund Balances', values: bs('total_liabilities_and_fund_balances'), indent: true, total: true },
          ]}
        />
      </SectionCard>
    </Box>
  );
};

export default FinancialsTab;
